import { defineStore } from "pinia";
import { watch } from "vue";
import { useUserDataStore } from "@/stores/userData.js";
import { subscribeToEvents } from "@/firestoreModel";
import { updateSeatBooking, addUserToInterest, removeUserFromInterest, addEvent, subscribeToProfile } from "@/firestoreModel";


export const useEventsStore = defineStore("events", {
    state: () => ({ events: [], eventsReady: false, eventsListener: null, profileListener:null, isUpdatingFromFirestore: false, lastChangedSeat: null, lastChangedInterest: null, changedInterestBatch: {}, profiles: [], newEvent: null }),
    actions: {
        setEvents(events) {
            this.events = events;
        },
        async init() {
            // Tell model to start listening to events
            const userDataStore = useUserDataStore();

            watch(
                () => [userDataStore.isLoaded],
                (newIsLoaded) => {
                    if (newIsLoaded) {
                        this.listenToEvents();
                    }
                },
                { immediate: true }
            );

            watch(() => [this.isUpdatingFromFirestore], (newVal) => {
            }, { immediate: true });

            watch(
                () => this.eventsReady,
                (val) => {
                    if (val) {
                        this.listenToProfile();
                    }
                }
            )

            watch(
                () => this.newEvent,
                async (val) => {
                    if(val != null){
                        await addEvent({
                            name: val.name,
                            description: val.description,
                            startDate: val.startDate,
                            endDate: val.endDate,
                            location: val.location,
                            imageUrl: val.imageUrl
                        })
                    }
                }
            )

            // Watch for changes in seat booking and persist to Firestore
            watch(
                () => this.lastChangedSeat,
                async (newVal) => {
                    if (newVal) {
                        await updateSeatBooking(
                            newVal.eventId,
                            newVal.spotId,
                            newVal.uid,
                            newVal.originalSpotId
                        );
                    }
                },
                { deep: true }
            );

            // Watch for changes in interests and persist to Firestore
            watch(
                () => this.lastChangedInterest,
                async (newVal) => {
                    if (newVal) {
                        if (newVal.action === 'add') {
                            await addUserToInterest(newVal.eventId, newVal.interest, newVal.userId);
                        } else if (newVal.action === 'remove') {
                            await removeUserFromInterest(newVal.eventId, [newVal.interestId], newVal.userId);
                        }
                    }
                },
                { deep: true }
            );
            watch(
                () => this.changedInterestBatch,
                async (batch) => {
                    if (batch != null) {
                        await removeUserFromInterest(batch.eventId, batch.interestIds, batch.userId)
                    }
                }
            )

        },
        addNewEvent(newCreatedEvent){
            this.newEvent = newCreatedEvent
        },
        updateSeatBooking(eventId, spotId, uid, originalSpotId) {
            // 1. Update Local State Immediately (Optimistic UI)
            const event = this.events.find(e => e.id === eventId);
            if (event && event.spots) {
                // Apply the same logic as the model to local state
                if (spotId !== null && spotId[0] !== null && event.spots[spotId[0]] && event.spots[spotId[0]].row[spotId[1]] !== undefined) {
                    event.spots[spotId[0]].row[spotId[1]] = uid;
                }

                if (originalSpotId && originalSpotId[0] !== null && originalSpotId[1] !== null && event.spots[originalSpotId[0]]) {
                    event.spots[originalSpotId[0]].row[originalSpotId[1]] = "";
                }
            }

            // 2. Trigger the watcher by updating the tracker state
            this.lastChangedSeat = {
                eventId,
                spotId,
                uid,
                originalSpotId,
                timestamp: Date.now() // Ensure watcher fires even if same data is sent twice
            };
        },
        getSeatBookingForUser(event, uid) {
            if (!event || !event.spots) {
                return null;
            }
            for (let i = 0; i < event.spots.length; i++) {
                for (let j = 0; j < event.spots[i].row.length; j++) {
                    if (event.spots[i].row[j] === uid) {
                        return [i, j];
                    }
                }
            }
            return null;
        },
        getInterests(event) {
            if (!event || !event.interests) {
                return [];
            } else {
                return event.interests;
            }
        },
        addUserToInterest(eventId, interest, userID) {
            // 1. Update Local State
            const event = this.events.find(e => e.id === eventId);
            if (event) {
                if (!event.interests) event.interests = [];

                const existingInterest = event.interests.find(i => i.id === interest.id);
                if (!existingInterest) {
                    // Create new interest
                    // Make sure we have a clean object structure locally
                    event.interests.push({
                        name: interest.name,
                        id: interest.id,
                        imageURL: interest.imageURL,
                        users: [userID]
                    });
                } else {
                    if (!existingInterest.users.includes(userID)) {
                        existingInterest.users.push(userID);
                    }
                }
            }

            // 2. Trigger Watcher
            this.lastChangedInterest = {
                action: 'add',
                eventId,
                interest,
                userId: userID,
                timestamp: Date.now()
            };
        },
        removeUserFromInterest(eventId, interestId, userID) {
            // 1. Update Local State
            const event = this.events.find(e => e.id === eventId);
            if (event && event.interests) {
                const existingInterest = event.interests.find(i => i.id === interestId);
                if (existingInterest) {
                    const userIndex = existingInterest.users.indexOf(userID);
                    if (userIndex !== -1) {
                        existingInterest.users.splice(userIndex, 1);
                        // Remove interest if empty
                        if (existingInterest.users.length === 0) {
                            const interestIndex = event.interests.indexOf(existingInterest);
                            if (interestIndex !== -1) {
                                event.interests.splice(interestIndex, 1);
                            }
                        }
                    }
                }
            }

            // 2. Trigger Watcher
            this.lastChangedInterest = {
                action: 'remove',
                eventId,
                interestId,
                userId: userID,
                timestamp: Date.now()
            };
        },
        removeUserFromBatchInterest(eventId, interestIds, userID) {
            const updateBatch = []
            interestIds.forEach(interest => {
                const interestId = interest.id
                // 1. Update Local State
                const event = this.events.find(e => e.id === eventId);
                if (event && event.interests) {
                    const existingInterest = event.interests.find(i => i.id === interestId);
                    if (existingInterest) {
                        const userIndex = existingInterest.users.indexOf(userID);
                        if (userIndex !== -1) {
                            existingInterest.users.splice(userIndex, 1);
                            // Remove interest if empty
                            if (existingInterest.users.length === 0) {
                                const interestIndex = event.interests.indexOf(existingInterest);
                                if (interestIndex !== -1) {
                                    event.interests.splice(interestIndex, 1);
                                }
                            }
                        }
                    }
                }
                updateBatch.push(interestId)
            })
            // 2. Trigger Watcher
            this.changedInterestBatch = {
                eventId: eventId,
                interestIds: updateBatch,
                userId: userID
            }
        },
        getUserProfilesByIds(userIds){
            const profiles = []
            userIds.forEach()

            return profiles
        },
        getEventById(id) {
            return this.events.filter((e) => e.id === id)[0];
        },
        listenToProfile(){
            if (this.profileListener) {
                this.profileListener();
                this.profileListener = null;
            }

            this.profileListener = subscribeToProfile((callbackProfile) => {this.profiles = callbackProfile});
        },

        listenToEvents() {
            if (this.eventsListener) {
                this.eventsListener();
                this.eventsListener = null;
            }
            

            function setEventsFromFirestore(events) {
                // Weird workaround for isUpdatingFromFirestore not seeming to update in time
                watch(
                    () => [this.isUpdatingFromFirestore],
                    (val) => {
                        if (val) {
                            this.setEvents(events);
                            this.isUpdatingFromFirestore = false;
                        }
                    },
                    { once: true }
                );

                this.isUpdatingFromFirestore = true;
            }


            // Create a callback that binds 'this' to model
            const callback = setEventsFromFirestore.bind(this);
            this.eventsListener = subscribeToEvents(callback);



            if (!this.eventsReady && this.eventsListener) {
                // Watch for the first time events are set to mark events as ready
                watch(
                    () => this.events,
                    () => {
                        this.eventsReady = true;
                    },
                    { once: true }
                );
            }
        },

        stopListeningToEvents() {
            if (this.eventsListener) {
                this.eventsListener();
                this.eventsListener = null;
            }
        },
    },
});
